import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { Alert, Button, Radio } from 'antd';
// Api
import { GET_TICKETING_OVERVIEW } from 'api/ticketing/queries';
// Types
import {
  GetTicketingOverview,
  GetTicketingOverviewVariables,
} from 'api/ticketing/types/GetTicketingOverview';
// Helpers
import {
  formatMoney,
  getTicketingErrorMessage,
  pluralize,
} from 'pages/Events/helpers';
// Constants
import { EVENTS } from 'constants/routes';
import { EventsTab } from 'pages/Events/constants';
// Components
import KpiCard from 'pages/Events/components/KpiCard/KpiCard';
// Styles
import styles from './TicketingOverview.module.scss';

const ALL_TIME = 'all';

const PERIOD_OPTIONS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: ALL_TIME, label: 'All time' },
];

const DEFAULT_PERIOD = '30';

const TicketingOverview = (): JSX.Element => {
  const history = useHistory();
  const [period, setPeriod] = useState(DEFAULT_PERIOD);

  const { data, loading, error } = useQuery<
    GetTicketingOverview,
    GetTicketingOverviewVariables
  >(GET_TICKETING_OVERVIEW, {
    variables:
      period === ALL_TIME
        ? {}
        : {
            input: {
              from: moment()
                .subtract(Number(period), 'days')
                .startOf('day')
                .toISOString(),
            },
          },
    fetchPolicy: 'cache-and-network',
  });

  const overview = data?.ticketingOverview;
  const isLoading = loading && !overview;

  const attention = overview
    ? [
        overview.failedRefunds > 0 &&
          `${pluralize(overview.failedRefunds, 'refund')} failed and ${
            overview.failedRefunds === 1 ? 'needs' : 'need'
          } a retry`,
        overview.inReviewOrders > 0 &&
          `${pluralize(overview.inReviewOrders, 'paid order')} in review`,
        overview.pendingRefunds > 0 &&
          `${pluralize(overview.pendingRefunds, 'refund')} waiting on Stripe`,
      ].filter(Boolean)
    : [];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>Ticket sales</span>
        <Radio.Group
          size="small"
          optionType="button"
          buttonStyle="solid"
          options={PERIOD_OPTIONS}
          value={period}
          onChange={(event) => setPeriod(event.target.value)}
        />
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={getTicketingErrorMessage(
            error,
            'Failed to load the ticket sales'
          )}
        />
      ) : (
        <KpiCard
          colProps={{ xs: 12, md: 6 }}
          loading={isLoading}
          items={[
            {
              title: 'Gross sales',
              value: formatMoney(overview?.grossRevenue ?? 0),
              caption: 'Charged to buyers before refunds',
            },
            {
              title: 'Host revenue',
              value: formatMoney(overview?.ticketRevenue ?? 0),
              caption: 'Tickets less discounts',
            },
            {
              title: 'Service fees',
              value: formatMoney(overview?.serviceFees ?? 0),
              caption: 'What MILLIONS keeps',
            },
            {
              title: 'Tickets sold',
              value: overview?.ticketsSold ?? 0,
              caption: pluralize(overview?.confirmedOrders ?? 0, 'order'),
            },
            {
              title: 'Refunded',
              value: formatMoney(overview?.refundedRevenue ?? 0),
              caption: pluralize(overview?.refundedOrders ?? 0, 'order'),
            },
            { title: 'Comps', value: overview?.compTickets ?? 0 },
            { title: 'Checked in', value: overview?.checkedIn ?? 0 },
            {
              title: 'On sale now',
              value: overview?.eventsOnSale ?? 0,
              caption: 'Events',
            },
          ]}
        />
      )}

      {attention.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={attention.join(' · ')}
          action={
            <Button
              size="small"
              onClick={() => history.push(`${EVENTS}/${EventsTab.Orders}`)}
            >
              Open orders
            </Button>
          }
        />
      )}
    </div>
  );
};

export default TicketingOverview;
